'use client';

/**
 * Form field primitives.
 *
 * `Field` wires the label, the control, the hint and the error message together
 * with real `id` / `aria-describedby` / `aria-invalid` attributes, so a screen
 * reader announces "Amount, required, edit text, Amount must be a number"
 * instead of leaving the error stranded next to the input.
 */
import * as React from 'react';
import * as LabelPrimitive from '@radix-ui/react-label';
import { AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

const FieldContext = React.createContext<{
  id: string;
  errorId: string;
  hintId: string;
  hasError: boolean;
  hasHint: boolean;
} | null>(null);

/**
 * The enclosing field, or a hard failure.
 *
 * For `FieldLabel` only. A label is a promise that it names a specific control,
 * and it keeps that promise through the `id` this context carries. Rendered with
 * no `Field` around it there is no id to point at, so the label names nothing —
 * which a screen reader cannot report and a sighted reader cannot see is wrong.
 * That is a mistake in the markup, and it should be loud.
 */
function useField() {
  const context = React.useContext(FieldContext);
  if (!context) throw new Error('Field controls must be rendered inside a <Field>.');
  return context;
}

export type FieldProps = {
  children: React.ReactNode;
  error?: string | undefined;
  hint?: string | undefined;
  className?: string;
  id?: string;
};

export function Field({ children, error, hint, className, id: providedId }: FieldProps) {
  const generatedId = React.useId();
  const id = providedId ?? generatedId;

  return (
    <FieldContext.Provider
      value={{
        id,
        errorId: `${id}-error`,
        hintId: `${id}-hint`,
        hasError: Boolean(error),
        hasHint: Boolean(hint),
      }}
    >
      <div className={cn('space-y-1.5', className)}>
        {children}
        {hint && !error ? (
          <p id={`${id}-hint`} className="text-xs text-ink-subtle">
            {hint}
          </p>
        ) : null}
        {error ? (
          <p id={`${id}-error`} className="flex items-start gap-1.5 text-xs font-medium text-danger-ink">
            <AlertCircle className="mt-px size-3.5 shrink-0" aria-hidden="true" />
            <span>{error}</span>
          </p>
        ) : null}
      </div>
    </FieldContext.Provider>
  );
}

export function FieldLabel({
  children,
  required,
  className,
  ...props
}: React.ComponentProps<typeof LabelPrimitive.Root> & { required?: boolean }) {
  const { id } = useField();
  return (
    <LabelPrimitive.Root
      htmlFor={id}
      className={cn('block text-sm font-medium text-ink', className)}
      {...props}
    >
      {children}
      {required ? (
        <>
          <span aria-hidden="true" className="ml-0.5 text-danger">
            *
          </span>
          <span className="sr-only"> (required)</span>
        </>
      ) : null}
    </LabelPrimitive.Root>
  );
}

/**
 * The aria wiring a control needs to participate in its field.
 *
 * Unlike `FieldLabel`, a control outside a `Field` is not a mistake. Two shapes
 * in this application are deliberately standalone: a search box whose only label
 * is a magnifying glass and an `aria-label`, and the quantity cell in a counting
 * grid, where the row heading already says which product the number belongs to.
 * Neither has a label, hint or error to be wired to, so there is nothing for this
 * hook to supply and it supplies nothing.
 *
 * It returns an empty object rather than throwing because the alternative is a
 * `Field` wrapper added purely to satisfy a context lookup — an extra element in
 * a table cell, around a control that was already accessible. The guard that
 * matters is the one on the label above, and it still fires.
 *
 * A control with neither a `Field` nor an `aria-label` is still wrong; that is
 * what the axe sweep in the end-to-end suite is for.
 */
export function useFieldControlProps() {
  const context = React.useContext(FieldContext);
  if (!context) return {} as const;

  const { id, errorId, hintId, hasError, hasHint } = context;
  const describedBy = [hasError ? errorId : null, hasHint && !hasError ? hintId : null].filter(Boolean).join(' ');
  return {
    id,
    'aria-invalid': hasError || undefined,
    'aria-describedby': describedBy || undefined,
  } as const;
}

const controlClasses =
  'block w-full rounded-[var(--radius-control)] border bg-surface px-3 text-sm text-ink transition-colors placeholder:text-ink-subtle disabled:cursor-not-allowed disabled:bg-surface-muted disabled:text-ink-subtle aria-[invalid=true]:border-danger';

export const Input = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(
  function Input({ className, ...props }, ref) {
    const fieldProps = useFieldControlProps();
    return (
      <input
        ref={ref}
        className={cn(controlClasses, 'min-h-11 border-border-strong', className)}
        {...fieldProps}
        {...props}
      />
    );
  },
);

/**
 * A money input. `inputMode="decimal"` gives a phone the numeric keypad, and
 * the value stays a string all the way to the server.
 */
export const MoneyInput = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(
  function MoneyInput({ className, ...props }, ref) {
    const fieldProps = useFieldControlProps();
    return (
      <div className="relative">
        <span
          aria-hidden="true"
          className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-ink-subtle"
        >
          ৳
        </span>
        <input
          ref={ref}
          type="text"
          inputMode="decimal"
          autoComplete="off"
          className={cn(controlClasses, 'min-h-11 border-border-strong pl-7 text-right tabular', className)}
          {...fieldProps}
          {...props}
        />
      </div>
    );
  },
);

export const NumberInput = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(
  function NumberInput({ className, ...props }, ref) {
    const fieldProps = useFieldControlProps();
    return (
      <input
        ref={ref}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        className={cn(controlClasses, 'min-h-11 border-border-strong text-right tabular', className)}
        {...fieldProps}
        {...props}
      />
    );
  },
);

export const Textarea = React.forwardRef<HTMLTextAreaElement, React.TextareaHTMLAttributes<HTMLTextAreaElement>>(
  function Textarea({ className, rows = 3, ...props }, ref) {
    const fieldProps = useFieldControlProps();
    return (
      <textarea
        ref={ref}
        rows={rows}
        className={cn(controlClasses, 'resize-y border-border-strong py-2.5', className)}
        {...fieldProps}
        {...props}
      />
    );
  },
);

/**
 * A native select. Deliberately native rather than a custom listbox: it is
 * keyboard- and screen-reader-correct for free, and on a phone it opens the OS
 * picker, which is much faster to use one-handed.
 */
export const Select = React.forwardRef<HTMLSelectElement, React.SelectHTMLAttributes<HTMLSelectElement>>(
  function Select({ className, children, ...props }, ref) {
    const fieldProps = useFieldControlProps();
    return (
      <select
        ref={ref}
        className={cn(
          controlClasses,
          'min-h-11 appearance-none border-border-strong bg-[length:16px] bg-[right_0.75rem_center] bg-no-repeat pr-9',
          className,
        )}
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%236b7685' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E\")",
        }}
        {...fieldProps}
        {...props}
      >
        {children}
      </select>
    );
  },
);

/** A form-wide error, announced politely when it appears. */
export function FormError({ message }: { message?: string | null }) {
  if (!message) return null;
  return (
    <div
      role="alert"
      className="flex items-start gap-2 rounded-[var(--radius-control)] border border-danger/30 bg-danger-soft px-3 py-2.5 text-sm text-danger-ink"
    >
      <AlertCircle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
      <span>{message}</span>
    </div>
  );
}
